import { action, observable, makeObservable, runInAction } from 'mobx';

import UserStore, { IUser, ITenants, Metadata } from 'stores/user/UserStore';
import { buildWhereFilter, get, getPage, getCount, patch, post } from 'stores/_services/dataService';
import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import { SortDirection } from 'constants/SortDirection';
import { getListConditions, getConditions } from 'stores/_services/filterService';

const FILTERED_ROLES = new Set('worker');
const SEARCH_FIELDS = ['email'];

export default class UserListStore extends BaseListStore<UserStore> {
    public currentUserStore?: UserStore | null = null;

    constructor() {
        super({
            sortBy: 'id',
            sortDirection: SortDirection.DESC,
            itemStore: UserStore
        });

        makeObservable(this, {
            currentUserStore: observable,
            getCurrentUser: action,
            getUsers: action,
            getUser: action,
            deleteUser: action,
            editUser: action,
            checkUserFieldExists: action
        });
    }

    get me(): IUser | undefined {
        return this.currentUserStore?.user;
    }

    async getCurrentUser(force?: boolean): Promise<void> {
        if (force || !this.me) {
            const options = {
                filter: {
                    fields: ['id', 'username', 'email', 'enabled', 'oAuthId', 'roleMappings', 'userPermissions'],
                    include: [
                        {
                            relation: 'roleMappings',
                            scope: {
                                fields: ['id', 'roleId', 'role'],
                                include: {
                                    relation: 'role',
                                    scope: {
                                        fields: ['id', 'name']
                                    }
                                }
                            }
                        },
                        {
                            relation: 'userPermissions',
                            scope: {
                                fields: ['resourceType', 'resourceId']
                            }
                        }
                    ]
                }
            };

            const { data } = await get('users/me', options);
            //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
            runInAction(() => {
                this.currentUserStore = new UserStore(data);
            });
            return data;
        }
    }

    async getUsers(args = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<string | void> {
        const shouldReset = this._updateListState(args);
        const isSuperAdmin = !!this.me?.email?.match(/@(infocyte|datto|kaseya).com/) && this.me?.isAdmin;
        const conditions = getConditions(this.filter);
        conditions.push({ deleted: false });

        if (!isSuperAdmin) {
            conditions.push({ and: [{ email: { nilike: '%infocyte.com%' } }, { email: { nilike: '%datto.com%' } }, { email: { nilike: '%kaseya.com%' } }] });
        }

        const options = {
            filter: {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: this.sort,
                fields: ['id', 'username', 'email', 'enabled', 'roleMappings', 'userPermissions'],
                include: [
                    {
                        relation: 'roleMappings',
                        scope: {
                            fields: ['id', 'roleId', 'role'],
                            include: {
                                relation: 'role',
                                scope: {
                                    fields: ['id', 'name']
                                }
                            }
                        }
                    },
                    {
                        relation: 'userPermissions',
                        scope: {
                            fields: ['resourceType', 'resourceId']
                        }
                    }
                ]
            }
        };

        const { data } = await getPage('users', options);
        const totalCount = await getCount('users', { where: conditions, searchFields: SEARCH_FIELDS });

        data.filter((user: IUser) => {
            for (const i in user.roleMappings) {
                const roleName = user.roleMappings[i].role.name;
                return !FILTERED_ROLES.has(roleName);
            }
        });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, 0);

        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async getUser(userId: string): Promise<string | void> {
        let res;

        const options = {
            filter: {
                fields: ['id', 'username', 'email', 'enabled', 'roleMappings', 'userPermissions'],
                include: [
                    {
                        relation: 'roleMappings',
                        scope: {
                            fields: ['id', 'roleId', 'role'],
                            include: {
                                relation: 'role',
                                scope: {
                                    fields: ['id', 'name']
                                }
                            }
                        }
                    },
                    {
                        relation: 'userPermissions',
                        scope: {
                            fields: ['resourceType', 'resourceId']
                        }
                    }
                ]
            }
        };

        try {
            res = await get(`users/${userId}`, options);
        } catch (e) {
            return (e as Error).message;
        }

        const { data } = res;

        this._updateData(data);
        return data;
    }

    async deleteUser(userId: string): Promise<string | undefined> {
        const userStore = this.findItem(userId) as UserStore;

        try {
            await userStore.delete();
        } catch (e) {
            return (e as Error).message;
        }

        this.getUsers({ reset: true });
    }

    async editUser({ id, username, email, role, password, userPermissions }: Partial<IUser>): Promise<undefined | string> {
        let res;
        const user = {
            id,
            username,
            email,
            role,
            password: password || undefined,
            userPermissions
        };

        try {
            res = await patch('users', user);
        } catch (e) {
            return (e as Error).message;
        }

        const { data } = res;

        if (data?.id) {
            this.getUser(data.id);
        }
    }

    async updateUserK1Exclusion(id: string[], value: boolean): Promise<string | undefined> {
        const conditions = getListConditions(Array.isArray(id) ? id : [id]);
        const where = buildWhereFilter({ where: conditions });

        try {
            await post(
                'users/update',
                { excludeK1Enforcement: value },
                {
                    queryParams: { where }
                }
            );
        } catch (e) {
            return (e as Error).message;
        }

        this.getUsers({ reset: true });
    }

    async checkUserFieldExists(field: keyof IUser, value = '', id?: string): Promise<boolean> {
        let res;

        if (!value) {
            return false;
        }

        try {
            res = await get('users', {
                filter: {
                    where: { [field]: { ilike: value }, deleted: false }
                }
            });
        } catch (e) {
            return false;
        }

        const { data } = res;

        return !!data.length && (!id || id !== data[0].id);
    }

    async getTenants(): Promise<ITenants[]> {
        const { data } = await get('users/me/tenants');
        return data;
    }

    async getMetadata(): Promise<Metadata> {
        const { data } = await get('users/me/metadata');
        return data;
    }
}
